import { PlusIcon } from "@heroicons/react/16/solid";
import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import BreadcrumbsWrapper from "~/components/BreadcrumbsV2/BreadcrumbsWrapper.tsx";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement.tsx";
import Button from "~/components/buttons/Button/Button.tsx";
import Card from "~/components/Card/Card.tsx";
import ContentWrapper from "~/components/ContentWrapper.tsx";
import SearchInput from "~/components/formElements/SearchInput/SearchInput.tsx";
import Headline from "~/components/Headline/Headline.tsx";
import Table from "~/components/Table/Table.tsx";
import { QUOTA_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { QUOTAS_INDEX_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import usePageStateStorage from "~/hooks/usePageStateStorage.tsx";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { CreateQuotaSidebar } from "~/modules/quota/components/CreateQuotaSidebar/CreateQuotaSidebar.tsx";
import { validQuotaDetailsNavigatedFromOrigins } from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import type { ColumnFilterType } from "~/types/tanstackTableTypes.ts";
import {
	createColumnFilterInputHandler,
	getColumnFilterValueForKey,
	sanitiseColumnFilters,
} from "~/utils/tanStackTableUtils.ts";

import QuotaCustomerFilter from "./components/QuotaCustomerFilter.tsx";
import QuotasIndexActiveFilters from "./components/QuotasIndexActiveFilters.tsx";
import QuotaStatusButtonFilter from "./components/QuotaStatusButtonFilter.tsx";
import { quotasIndexTableColumnDefs } from "./quotasIndexTableColumnDefs.tsx";
import type { QuotaListStatus } from "./quotasIndexTableTypes.ts";
import { mapQuotaToTableData, quotasIndexDefaultPageState } from "./quotasIndexUtils.tsx";

type Props = {
	quotas: QuotaWithRelations[];
};

const QuotasIndexView: FunctionComponent<Props> = ({ quotas }) => {
	const [showCreateQuotaSidebar, setShowCreateQuotaSidebar] = useState<boolean>(false);
	const navigate = useNavigate();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const { pageState, setPageState } = usePageStateStorage({
		pageKey: QUOTAS_INDEX_PAGE_STATE_KEY,
		defaultState: quotasIndexDefaultPageState,
	});

	const [columnFilters, setColumnFilters] = useState<ColumnFilterType[]>(
		sanitiseColumnFilters(quotasIndexDefaultPageState.columnFilters, pageState.columnFilters));
	const [globalFilter, setGlobalFilter] = useState<string>(pageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState(prevState => ({ ...prevState, columnFilters, globalFilter, sorting }));
	}, [columnFilters, globalFilter, setPageState, sorting]);

	const handleColumnFilterInputChange = createColumnFilterInputHandler(columnFilters, setColumnFilters);

	const tableData = useMemo(() => quotas.map(mapQuotaToTableData), [quotas]);

	const handleRowClick = useCallback(
		(row: Row<ReturnType<typeof mapQuotaToTableData>>, event: MouseEvent) => {
			const path = QUOTA_DETAILS_PATH_WITH_ORIGIN(row.original.companyId,
				row.original.id,
				validQuotaDetailsNavigatedFromOrigins.QUOTAS_INDEX);
			if (event.ctrlKey || event.metaKey) {
				window.open(path, "_blank");
			} else {
				navigate(path);
			}
		},
		[navigate],
	);

	const table = useReactTable({
		data: tableData,
		columns: quotasIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			columnFilters,
			globalFilter,
			sorting,
		},
		globalFilterFn: (row, _columnId, filterValue) => {
			const search = String(filterValue).trim().toLowerCase();
			if (!search) return true;
			return row.original.searchString.includes(search);
		},
		meta: {
			onRowClick: handleRowClick,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	const { rows } = table.getRowModel();

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollContainerRef.current,
		estimateSize: () => 48.5,
		overscan: 10,
	});

	const statusFilterValue = getColumnFilterValueForKey("status", columnFilters) as QuotaListStatus[];
	const customerFilterValue = getColumnFilterValueForKey("companyShortName", columnFilters) as string[];

	return <div className="grid h-screen grid-rows-[auto_auto_1fr] overflow-hidden">
		<div className="bg-white">
			<ContentWrapper>
				<BreadcrumbsWrapper>
					<BreadcrumbElement text="Abrechnungskontingente" />
				</BreadcrumbsWrapper>
			</ContentWrapper>
			<ContentWrapper className={clsx("flex w-full items-center justify-between gap-4 pb-4 pt-2")}>
				<Headline type="h2">Abrechnungskontingente</Headline>
				<Button onClick={() => setShowCreateQuotaSidebar(true)}>
					<PlusIcon className="size-5" /><span>Neues Abrechnungskontingent</span>
				</Button>
			</ContentWrapper>
		</div>

		<ContentWrapper className="z-50 mb-2 mt-4 w-full px-8">
			<Card shadow="sm"
				  padding="sm">
				<div className="flex w-full items-center gap-x-4">
					<div className="w-64">
						<SearchInput value={globalFilter}
									 inputSize="sm"
									 autoFocus={true}
									 onChange={setGlobalFilter} />
					</div>
					<div className="w-64">
						<QuotaCustomerFilter onChange={(value) => handleColumnFilterInputChange("companyShortName", value)}
											 filterValues={customerFilterValue} />
					</div>
					<QuotaStatusButtonFilter onChange={(value) => handleColumnFilterInputChange("status", value)}
											 filterValues={statusFilterValue} />
				</div>
			</Card>
			<QuotasIndexActiveFilters customerFilterValue={customerFilterValue}
									  statusFilterValue={statusFilterValue}
									  onChangeCustomerFilter={(value) => handleColumnFilterInputChange("companyShortName", value)}
									  onChangeStatusFilter={(value) => handleColumnFilterInputChange("status", value)} />
		</ContentWrapper>

		<ContentWrapper className="size-full overflow-hidden px-8 pb-6">
			<Table table={table}
				   fixedWidth={true}
				   virtualized={true}
				   scrollContainerRef={scrollContainerRef}
				   virtualizer={virtualizer} />
		</ContentWrapper>
		<CreateQuotaSidebar isOpen={showCreateQuotaSidebar} onClose={()=>setShowCreateQuotaSidebar(false)}/>
	</div>;
};

export default QuotasIndexView;
